import React from "react";
import { Link } from "react-router-dom";
import * as moment from "moment";
import LTAStatusBadge from "pages/LTAs/components/LTAStatusBadge";
import PSEARatingBadge from "pages/Assessments/PSEAAssessments/PSEARatingBadge";
import {
  CompareRowModel,
  CompareSectionModel,
  Empty,
  Outcome,
} from "./CompareLayout";
import { formatAddress, mergeUniqueBy } from "./mergeRules";
import type { MovingItem } from "./ConfirmMergeModal";

export interface Side {
  record: any;
  summary: any;
  ltas: any;
  psea: any;
}

interface BuildOptions {
  main: Side;
  merge: Side;
  canViewLtas: boolean;
  canViewPsea: boolean;
  pseaKeptSide: "primary" | "duplicate" | null;
}

const norm = (v: any) =>
  (typeof v === "string" ? v : v?.toString?.() ?? "").trim().toLowerCase();

const sameSet = (a: string[], b: string[]) => {
  const left = a.map(norm).filter(Boolean).sort().join("|");
  const right = b.map(norm).filter(Boolean).sort().join("|");
  return left === right;
};

const formatMoney = (value: number) =>
  `$${(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const formatDate = (value?: string) =>
  value ? moment(value).format("DD MMM YYYY") : null;

const ValueList = ({ items }: { items: React.ReactNode[] }) =>
  items.length === 0 ? (
    <Empty />
  ) : (
    <ul className="list-unstyled mb-0 vstack gap-2">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );

/** A status tag: tint background, accessible ink text (see DESIGN.md). */
const Tag = ({
  tone,
  icon,
  children,
}: {
  tone: "success" | "danger" | "warning" | "info" | "muted";
  icon?: string;
  children: React.ReactNode;
}) => (
  <span
    className={`badge ${
      tone === "muted" ? "bg-light" : `bg-${tone}-subtle`
    } text-ink-${tone}`}
  >
    {icon ? <i className={`${icon} me-1`} aria-hidden="true"></i> : null}
    {children}
  </span>
);

const MovesBadge = ({
  count,
  noun,
  singular,
}: {
  count: number;
  noun: string;
  singular: string;
}) =>
  count > 0 ? (
    <Tag tone="success" icon="ri-arrow-left-line">
      {count.toLocaleString()} {count === 1 ? `${singular} moves` : `${noun} move`}{" "}
      to main
    </Tag>
  ) : (
    <Tag tone="muted">Nothing to move</Tag>
  );

// --- Row builders --------------------------------------------------------

/**
 * A single-value field. "keep" fields always keep the main value; "fill"
 * fields take the merged contractor's value only when main has none.
 */
const scalarRow = (
  key: string,
  label: string,
  mainValue: any,
  mergeValue: any,
  rule: "keep" | "fill" | "info",
  /**
   * What happens to a discarded value: kept as a new alias on main, or
   * already on main under another field (acronym or an existing alias).
   */
  discarded: "alias" | "present" | null = null
): CompareRowModel => {
  const same = norm(mainValue) === norm(mergeValue);
  let merge: React.ReactNode = mergeValue || <Empty />;

  if (mergeValue && !same && rule !== "info") {
    if (rule === "fill" && !mainValue) {
      merge = (
        <>
          <div>{mergeValue}</div>
          <Outcome tone="success" icon="ri-arrow-left-line">
            Added to main
          </Outcome>
        </>
      );
    } else if (discarded === "present") {
      merge = (
        <>
          <div>{mergeValue}</div>
          <Outcome tone="secondary" icon="ri-check-double-line">
            Already on main as an acronym or alias
          </Outcome>
        </>
      );
    } else if (discarded === "alias") {
      merge = (
        <>
          <div>{mergeValue}</div>
          <Outcome tone="info" icon="ri-price-tag-3-line">
            Kept as an alias
          </Outcome>
        </>
      );
    } else {
      merge = (
        <>
          <div className="text-ink-muted">{mergeValue}</div>
          <Outcome tone="secondary" icon="ri-close-circle-line">
            {mainValue ? "Not kept — main value stays" : "Not carried over"}
          </Outcome>
        </>
      );
    }
  }

  return {
    key,
    label,
    main: mainValue || <Empty />,
    merge,
    same,
    empty: !mainValue && !mergeValue,
  };
};

/** A repeatable field: entries not already on main are added to it. */
const listRow = (
  key: string,
  label: string,
  mainItems: any[] = [],
  mergeItems: any[] = [],
  keyField: string,
  format: (item: any) => string = (item) => item?.[keyField]
): CompareRowModel => {
  const merged = mergeUniqueBy(mainItems, mergeItems, keyField);
  const added = new Set(
    merged.filter((m) => m.fromDuplicate).map((m) => m.item)
  );
  // Entries saved without a value are dropped by the merge, so they are shown
  // as blank rather than claimed to be on main already.
  const isBlank = (item: any) => !norm(item?.[keyField]);
  const hasValues = (items: any[] = []) =>
    (items || []).some((item) => !isBlank(item));
  return {
    key,
    label,
    main: (
      <ValueList
        items={(mainItems || []).map((item) =>
          isBlank(item) ? (
            <span className="text-ink-muted fst-italic">Blank entry</span>
          ) : (
            format(item)
          )
        )}
      />
    ),
    merge: (
      <ValueList
        items={(mergeItems || []).map((item) =>
          isBlank(item) ? (
            <>
              <div className="text-ink-muted fst-italic">Blank entry</div>
              <Outcome tone="secondary" icon="ri-close-circle-line">
                Not kept
              </Outcome>
            </>
          ) : added.has(item) ? (
            <>
              <div>{format(item)}</div>
              <Outcome tone="success" icon="ri-arrow-left-line">
                Added to main
              </Outcome>
            </>
          ) : (
            <>
              <div className="text-ink-muted">{format(item)}</div>
              <Outcome tone="secondary" icon="ri-check-double-line">
                Already on main
              </Outcome>
            </>
          )
        )}
      />
    ),
    same: sameSet(
      (mainItems || []).filter((i) => !isBlank(i)).map(format),
      (mergeItems || []).filter((i) => !isBlank(i)).map(format)
    ),
    empty: !hasValues(mainItems) && !hasValues(mergeItems),
  };
};

/** A count of related records; `moves` marks records the merge transfers. */
const countRow = (
  key: string,
  label: string,
  mainCount: number,
  mergeCount: number,
  moves: boolean,
  format: (n: number) => string = (n) => (n || 0).toLocaleString()
): CompareRowModel => ({
  key,
  label,
  main: <span className="fw-medium">{format(mainCount)}</span>,
  merge: (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span className="fw-medium">{format(mergeCount)}</span>
      {moves && mergeCount > 0 ? (
        <Outcome tone="success" icon="ri-arrow-left-line">
          Moves to main
        </Outcome>
      ) : null}
    </div>
  ),
  same: (mainCount || 0) === (mergeCount || 0),
  empty: false,
});

/** A count that signals risk: shown in danger ink whenever it is non-zero. */
const riskCountRow = (
  key: string,
  label: string,
  mainCount: number,
  mergeCount: number,
  isRisk = true
): CompareRowModel => {
  const cell = (count: number) => (
    <span
      className={`fw-medium ${isRisk && count > 0 ? "text-ink-danger fw-semibold" : ""}`}
    >
      {isRisk && count > 0 ? (
        <i className="ri-alarm-warning-line me-1" aria-hidden="true"></i>
      ) : null}
      {(count || 0).toLocaleString()}
    </span>
  );
  return {
    key,
    label,
    main: cell(mainCount),
    merge: (
      <div className="d-flex align-items-center gap-2 flex-wrap">
        {cell(mergeCount)}
        {mergeCount > 0 ? (
          <Outcome tone="success" icon="ri-arrow-left-line">
            Moves to main
          </Outcome>
        ) : null}
      </div>
    ),
    same: (mainCount || 0) === (mergeCount || 0),
    empty: false,
  };
};

const FlagCell = ({ record }: { record: any }) =>
  record?.rmuFlag ? (
    <div className="vstack gap-1">
      <span>
        <Tag tone="danger" icon="ri-flag-fill">
          Flagged by RMU
        </Tag>
      </span>
      {record?.flagDate ? (
        <span className="text-ink-muted fs-12">
          <i className="ri-time-line align-bottom me-1" aria-hidden="true"></i>
          {formatDate(record.flagDate)}
        </span>
      ) : null}
      {record?.flaggingJustification ? (
        <span
          className="compare-clamp fs-13"
          title={record.flaggingJustification}
        >
          {record.flaggingJustification}
        </span>
      ) : null}
    </div>
  ) : (
    <span className="text-ink-muted">Not flagged</span>
  );

const isAdverse = (rating: any) => rating?.performanceRatingType?.isFlag === true;

/** Related records listed in full on each side; all of them move. */
const recordsRow = (
  key: string,
  label: string,
  mainItems: any[],
  mergeItems: any[],
  render: (item: any) => React.ReactNode,
  footer?: { main?: React.ReactNode; merge?: React.ReactNode }
): CompareRowModel => ({
  key,
  label,
  main: (
    <>
      <ValueList items={(mainItems || []).map(render)} />
      {footer?.main}
    </>
  ),
  merge: (
    <>
      <ValueList items={(mergeItems || []).map(render)} />
      {footer?.merge}
    </>
  ),
  same: false,
  empty: !mainItems?.length && !mergeItems?.length,
});

// --- Item renderers ------------------------------------------------------

const renderRegistration = (reg: any) => (
  <>
    <div className="fw-medium">
      {reg?.registrationName || reg?.registrationType || "Registration"}
    </div>
    <div className="text-ink-muted fs-12">
      {[
        reg?.registrationNumber ? `#${reg.registrationNumber}` : null,
        formatDate(reg?.registrationDate),
        reg?.countryOfRegistration,
      ]
        .filter(Boolean)
        .join(" · ") || "No details"}
    </div>
  </>
);

const renderLta = (lta: any) => (
  <div className="d-flex align-items-start gap-2">
    <div className="flex-grow-1 merge-shrink">
      <Link
        to={`/ltas/${lta.id}`}
        className="fw-medium d-block text-truncate"
        title={lta?.title}
      >
        {lta?.title || "Untitled agreement"}
      </Link>
      <span className="text-ink-muted fs-12">
        {lta?.ltaNumber || "No number"}
        {lta?.leadAgency?.name ? ` · ${lta.leadAgency.name}` : ""}
      </span>
    </div>
    <LTAStatusBadge startDate={lta?.startDate} endDate={lta?.endDate} />
  </div>
);

const renderSemg = (item: any) => (
  <>
    <div className="fw-medium">{item?.reportNumber || "Unnumbered report"}</div>
    <div className="text-ink-muted fs-12">
      {[
        item?.SEMGSection,
        item?.SEMGPage ? `p. ${item.SEMGPage}` : null,
        item?.SEMGParagraph ? `para. ${item.SEMGParagraph}` : null,
      ]
        .filter(Boolean)
        .join(" · ") || "No details"}
    </div>
  </>
);

const renderDirector = (director: any) => (
  <>
    {director?.person?.id ? (
      <Link to={`/persons/${director.person.id}`} className="fw-medium">
        {director.person.fullName || "Unnamed person"}
      </Link>
    ) : (
      <span className="fw-medium">
        {director?.person?.fullName || "Unnamed person"}
      </span>
    )}
    <div className="text-ink-muted fs-12">
      {director?.role || "No role"}
      {director?.fromDate
        ? ` · ${moment(director.fromDate).format("MMM YYYY")} – ${
            director?.toDate
              ? moment(director.toDate).format("MMM YYYY")
              : "present"
          }`
        : ""}
    </div>
  </>
);

const moreFooter = (ltas: any) =>
  ltas?.total > (ltas?.items?.length || 0) ? (
    <div className="text-ink-muted fs-12 mt-2">
      and {(ltas.total - ltas.items.length).toLocaleString()} more
    </div>
  ) : null;

const NO_RATING_TYPE = "No type recorded";
const ratingTypeName = (rating: any) =>
  rating?.performanceRatingType?.name || NO_RATING_TYPE;

// --- Sections ------------------------------------------------------------

const SECTION_ORDER = ["profile", "contact", "overview", "performance", "risk"];

export const buildSections = ({
  main,
  merge,
  canViewLtas,
  canViewPsea,
  pseaKeptSide,
}: BuildOptions): CompareSectionModel[] => {
  const m = main.record || {};
  const d = merge.record || {};
  const sections: CompareSectionModel[] = [];

  // Mirrors the backend alias step: a merged name already recorded on main
  // (as its name, acronym or an alias) is not added again.
  const mainNames = new Set(
    [
      m.contractorName,
      m.acronym,
      ...(m.previousOrAliasName || []).map((alias: any) => alias?.name),
    ]
      .map(norm)
      .filter(Boolean)
  );
  const onMainAlready = (value: any) => mainNames.has(norm(value));

  // Risk signals lead the page: a merge must never hide or lose them.
  const mainAdverse = (m.performanceRatings || []).filter(isAdverse).length;
  const mergeAdverse = (d.performanceRatings || []).filter(isAdverse).length;
  const anyRisk =
    m.rmuFlag ||
    d.rmuFlag ||
    mainAdverse + mergeAdverse > 0 ||
    (m.semgMentions?.length || 0) + (d.semgMentions?.length || 0) > 0;

  let flagOutcome: React.ReactNode = null;
  if (d.rmuFlag && !m.rmuFlag) {
    flagOutcome = (
      <Outcome tone="danger" icon="ri-flag-line">
        Flag moves to main
      </Outcome>
    );
  } else if (d.rmuFlag && m.rmuFlag) {
    flagOutcome = (
      <Outcome tone="secondary" icon="ri-file-add-line">
        Reasons added to main's flag
      </Outcome>
    );
  }

  sections.push({
    key: "risk",
    title: "Risk signals",
    icon: "ri-alarm-warning-line",
    tone: anyRisk ? "danger" : "primary",
    badge: anyRisk ? (
      <Tag tone="danger" icon="ri-alarm-warning-line">
        Risk on record
      </Tag>
    ) : (
      <Tag tone="muted">No risk signals</Tag>
    ),
    rows: [
      {
        key: "rmuFlag",
        label: "RMU flag",
        main: <FlagCell record={m} />,
        merge: (
          <>
            <FlagCell record={d} />
            {flagOutcome ? <div className="mt-1">{flagOutcome}</div> : null}
          </>
        ),
        same: !!m.rmuFlag === !!d.rmuFlag,
        empty: false,
      },
      riskCountRow("adverse", "Adverse ratings", mainAdverse, mergeAdverse),
      riskCountRow(
        "semgRisk",
        "SEMG mentions",
        m?.semgMentions?.length || 0,
        d?.semgMentions?.length || 0
      ),
    ],
  });

  // Overview: headline counts, and which of them move.
  const movingCounts = [
    d?.contracts?.count || 0,
    merge.summary?.totalAssessments || 0,
    canViewLtas ? merge.ltas?.total || 0 : 0,
    d?.performanceRatings?.length || 0,
    d?.semgMentions?.length || 0,
    d?.boardOfDirectors?.length || 0,
  ].reduce((a, b) => a + b, 0);

  sections.push({
    key: "overview",
    title: "Overview",
    icon: "ri-dashboard-line",
    badge: <MovesBadge count={movingCounts} noun="records" singular="record" />,
    rows: [
      countRow(
        "contracts",
        "Contracts",
        m?.contracts?.count || 0,
        d?.contracts?.count || 0,
        true
      ),
      countRow(
        "value",
        "Contract value",
        main.summary?.totalValueOfContracts || 0,
        merge.summary?.totalValueOfContracts || 0,
        false,
        formatMoney
      ),
      countRow(
        "assessments",
        "Assessments",
        main.summary?.totalAssessments || 0,
        merge.summary?.totalAssessments || 0,
        true
      ),
      countRow(
        "agencies",
        "UN agencies",
        main.summary?.totalAgencies || 0,
        merge.summary?.totalAgencies || 0,
        false
      ),
      ...(canViewLtas
        ? [
            countRow(
              "ltas",
              "LTAs",
              main.ltas?.total || 0,
              merge.ltas?.total || 0,
              true
            ),
          ]
        : []),
      countRow(
        "ratings",
        "Performance ratings",
        m?.performanceRatings?.length || 0,
        d?.performanceRatings?.length || 0,
        true
      ),
      countRow(
        "board",
        "Staff / Board",
        m?.boardOfDirectors?.length || 0,
        d?.boardOfDirectors?.length || 0,
        true
      ),
    ],
  });

  // Profile: the main contractor's identity is kept.
  sections.push({
    key: "profile",
    title: "Profile",
    icon: "ri-building-line",
    rows: [
      scalarRow(
        "name",
        "Contractor name",
        m.contractorName,
        d.contractorName,
        "keep",
        onMainAlready(d.contractorName) ? "present" : "alias"
      ),
      scalarRow(
        "acronym",
        "Acronym",
        m.acronym,
        d.acronym,
        "fill",
        onMainAlready(d.acronym) ? "present" : "alias"
      ),
      listRow(
        "aliases",
        "Other names",
        m.previousOrAliasName,
        d.previousOrAliasName,
        "name"
      ),
      scalarRow(
        "orgType",
        "Organization type",
        m?.organizationType?.name,
        d?.organizationType?.name,
        "keep"
      ),
      scalarRow("website", "Website", m.website, d.website, "fill"),
      scalarRow("created", "Added", formatDate(m.createdAt), formatDate(d.createdAt), "info"),
      scalarRow(
        "updated",
        "Last updated",
        formatDate(m.updatedAt),
        formatDate(d.updatedAt),
        "info"
      ),
    ],
  });

  // Contact details: entries not already on main are added.
  const contactRows = [
    listRow("phone", "Phone", m.phoneNumber, d.phoneNumber, "name"),
    listRow("email", "Email", m.emailAddress, d.emailAddress, "name"),
    listRow(
      "physical",
      "Physical address",
      m.physicalAddress,
      d.physicalAddress,
      "address",
      formatAddress
    ),
    listRow(
      "postal",
      "Postal address",
      m.postalAddress,
      d.postalAddress,
      "address",
      formatAddress
    ),
    listRow("contacts", "Contact persons", m.contactPersons, d.contactPersons, "name"),
  ];
  const contactAdditions = [
    ["phoneNumber", "name"],
    ["emailAddress", "name"],
    ["physicalAddress", "address"],
    ["postalAddress", "address"],
    ["contactPersons", "name"],
  ].reduce(
    (sum, [field, keyField]) =>
      sum +
      mergeUniqueBy(m[field], d[field], keyField).filter((x) => x.fromDuplicate)
        .length,
    0
  );
  sections.push({
    key: "contact",
    title: "Contact details",
    icon: "ri-contacts-book-line",
    badge:
      contactAdditions > 0 ? (
        <Tag tone="success">{contactAdditions} added to main</Tag>
      ) : (
        <Tag tone="muted">No new details</Tag>
      ),
    rows: contactRows,
  });

  // Registrations are all appended.
  const mergeRegistrations = d.contractorRegistration || [];
  sections.push({
    key: "registrations",
    title: "Registrations",
    icon: "ri-file-shield-2-line",
    badge: <MovesBadge count={mergeRegistrations.length} noun="registrations" singular="registration" />,
    rows: [
      recordsRow(
        "registrations",
        "Registrations",
        m.contractorRegistration,
        mergeRegistrations,
        renderRegistration
      ),
    ],
  });

  if (canViewLtas) {
    sections.push({
      key: "ltas",
      title: "LTAs",
      icon: "ri-file-paper-2-line",
      badge: <MovesBadge count={merge.ltas?.total || 0} noun="LTAs" singular="LTA" />,
      rows: [
        recordsRow(
          "ltas",
          "Agreements",
          main.ltas?.items,
          merge.ltas?.items,
          renderLta,
          { main: moreFooter(main.ltas), merge: moreFooter(merge.ltas) }
        ),
      ],
    });
  }

  if (canViewPsea) {
    const mp = main.psea;
    const dp = merge.psea;
    let badge: React.ReactNode = <Tag tone="muted">No change</Tag>;
    let mainTag: React.ReactNode = null;
    let mergeTag: React.ReactNode = null;
    if (mp && dp) {
      badge = <Tag tone="warning">One assessment kept</Tag>;
      const kept = (
        <Tag tone="success" icon="ri-check-line">
          Kept
        </Tag>
      );
      const superseded = <Tag tone="warning">Superseded — history kept</Tag>;
      mainTag = pseaKeptSide === "primary" ? kept : superseded;
      mergeTag = pseaKeptSide === "duplicate" ? kept : superseded;
    } else if (dp) {
      badge = <MovesBadge count={1} noun="assessment" singular="assessment" />;
      mergeTag = (
        <Outcome tone="success" icon="ri-arrow-left-line">
          Moves to main
        </Outcome>
      );
    }
    sections.push({
      key: "psea",
      title: "PSEA assessment",
      icon: "ri-shield-user-line",
      badge,
      mainTag,
      mergeTag,
      rows: [
        {
          key: "rating",
          label: "Overall rating",
          main: mp ? <PSEARatingBadge rating={mp.overallRating} /> : <Empty />,
          merge: dp ? <PSEARatingBadge rating={dp.overallRating} /> : <Empty />,
          same: norm(mp?.overallRating) === norm(dp?.overallRating),
          empty: !mp && !dp,
        },
        scalarRow(
          "assessed",
          "Date of assessment",
          formatDate(mp?.dateOfAssessment),
          formatDate(dp?.dateOfAssessment),
          "info"
        ),
        scalarRow(
          "agency",
          "Assessing agency",
          mp?.agency?.name,
          dp?.agency?.name,
          "info"
        ),
      ],
    });
  }

  const mergeRatings = d.performanceRatings || [];
  sections.push({
    key: "performance",
    title: "Performance ratings",
    icon: "ri-star-line",
    badge: <MovesBadge count={mergeRatings.length} noun="ratings" singular="rating" />,
    // Built from the ratings themselves rather than a fixed list, so a new
    // type (e.g. "Globally Blacklisted") or an untyped rating is never
    // silently dropped. Flag types lead and read as risk.
    rows: Array.from(
      new Map(
        [...(m.performanceRatings || []), ...mergeRatings].map((r: any) => [
          ratingTypeName(r),
          isAdverse(r),
        ])
      )
    )
      .sort(
        ([nameA, riskA], [nameB, riskB]) =>
          Number(riskB) - Number(riskA) ||
          Number(nameA === NO_RATING_TYPE) - Number(nameB === NO_RATING_TYPE) ||
          nameA.localeCompare(nameB)
      )
      .map(([name, risk]) =>
        riskCountRow(
          `rating-${name}`,
          name,
          (m.performanceRatings || []).filter(
            (r: any) => ratingTypeName(r) === name
          ).length,
          mergeRatings.filter((r: any) => ratingTypeName(r) === name).length,
          risk
        )
      ),
  });

  sections.push({
    key: "semg",
    title: "SEMG mentions",
    icon: "ri-alarm-warning-line",
    badge: (
      <MovesBadge count={d.semgMentions?.length || 0} noun="mentions" singular="mention" />
    ),
    rows: [
      recordsRow("semg", "Mentions", m.semgMentions, d.semgMentions, renderSemg),
    ],
  });

  sections.push({
    key: "board",
    title: "Staff / Board of directors",
    icon: "ri-team-line",
    badge: (
      <MovesBadge count={d.boardOfDirectors?.length || 0} noun="people" singular="person" />
    ),
    rows: [
      recordsRow(
        "board",
        "People",
        m.boardOfDirectors,
        d.boardOfDirectors,
        renderDirector
      ),
    ],
  });

  // Display order. Sections not listed here follow in the order built above.
  const rank = (key: string) => {
    const index = SECTION_ORDER.indexOf(key);
    return index === -1 ? SECTION_ORDER.length : index;
  };
  return sections
    .map((section, built) => ({ section, built }))
    .sort((a, b) => rank(a.section.key) - rank(b.section.key) || a.built - b.built)
    .map(({ section }) => section);
};

/**
 * What the merge carries over besides related records: the RMU flag, and the
 * names that become aliases on main. Mirrors the backend alias step (the
 * merged name, plus its acronym when main already has one, plus the merged
 * contractor's own aliases), skipping anything main already records.
 */
export const describeTransfers = (main: Side, merge: Side) => {
  const m = main.record || {};
  const d = merge.record || {};
  const known = new Set(
    [
      m.contractorName,
      m.acronym,
      ...(m.previousOrAliasName || []).map((alias: any) => alias?.name),
    ]
      .map(norm)
      .filter(Boolean)
  );
  const candidates = [
    d.contractorName,
    m.acronym ? d.acronym : null,
    ...(d.previousOrAliasName || []).map((alias: any) => alias?.name),
  ];
  const added = new Set<string>();
  candidates.forEach((name) => {
    const key = norm(name);
    if (key && !known.has(key)) added.add(key);
  });
  return {
    flag: (d.rmuFlag
      ? m.rmuFlag
        ? "reasons"
        : "moves"
      : null) as "moves" | "reasons" | null,
    aliases: added.size,
  };
};

/** The records that move to the main contractor, for the confirmation summary. */
export const countMoving = (merge: Side, canViewLtas: boolean): MovingItem[] => {
  const d = merge.record || {};
  const items: MovingItem[] = [
    {
      key: "contracts",
      label: "Contracts",
      icon: "ri-file-list-3-line",
      count: d?.contracts?.count || 0,
    },
  ];
  if (canViewLtas) {
    items.push({
      key: "ltas",
      label: "LTAs",
      icon: "ri-file-paper-2-line",
      count: merge.ltas?.total || 0,
    });
  }
  items.push(
    {
      key: "assessments",
      label: "Assessments",
      icon: "ri-survey-line",
      count: merge.summary?.totalAssessments || 0,
    },
    {
      key: "performance",
      label: "Performance ratings",
      icon: "ri-star-line",
      count: d?.performanceRatings?.length || 0,
    },
    {
      key: "semg",
      label: "SEMG mentions",
      icon: "ri-alarm-warning-line",
      count: d?.semgMentions?.length || 0,
    },
    {
      key: "board",
      label: "Staff / Board",
      icon: "ri-team-line",
      count: d?.boardOfDirectors?.length || 0,
    },
    {
      key: "registrations",
      label: "Registrations",
      icon: "ri-file-shield-2-line",
      count: d?.contractorRegistration?.length || 0,
    }
  );
  return items;
};
